<template>
  <div class="c-kdo-doc-templates">
    <n-loader :loading="$var('loadTemplates')" />

    <div class="kdo-toolbar">
      <div class="kdo-hint grow">
        Шаблон документа — файл Word с макросами <span class="kdo-macro">${ключ}</span>, маршрут согласования и набор
        полей, которые заполняет пользователь при создании документа.
      </div>
      <button type="button" class="kdo-btn" @click="openDefaults">Из типовых документов</button>
      <button type="button" class="kdo-btn primary" @click="openEditor(null)">+ Добавить шаблон</button>
    </div>

    <div class="kdo-card">
      <table>
        <thead>
          <tr>
            <th>Название</th>
            <th>Маршрут</th>
            <th>Файл</th>
            <th>Полей</th>
            <th>Документов</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in rows" :key="item.id">
            <td>{{ item.title }}</td>
            <td>{{ item.approvalTemplateTitle }}</td>
            <td><a v-if="item.fileSrc" :href="item.fileSrc" target="_blank">{{ item.fileName }}</a></td>
            <td>{{ item.assignedMetaFields.length }}</td>
            <td>{{ item.documentsCount }}</td>
            <td class="actions">
              <button type="button" class="kdo-btn small" @click="openEditor(item)">Изменить</button>
              <button
                type="button"
                class="kdo-btn small"
                title="Добавить этот документ в каталог типовых — его можно будет создать для любой компании одним нажатием"
                @click="makeDefault = { item, title: item.title, description: '', }">
                Сделать типовым
              </button>
              <button type="button" class="kdo-btn small danger" @click="toDelete = item">Удалить</button>
            </td>
          </tr>
          <tr v-if="!rows.length">
            <td colspan="6" class="empty">Шаблонов пока нет</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="form" class="kdo-overlay" @mousedown.self="closeEditor">
      <div class="kdo-dialog wide">
        <h3>{{ form.id ? 'Изменить шаблон документа' : 'Новый шаблон документа' }}</h3>
        <div class="kdo-hint">Файл, маршрут и поля. Сверка макросов покажет, всё ли в документе будет заполнено.</div>
        <a class="kdo-help-link" :href="helpHref('substitution')" target="_blank" rel="noopener">
          📖 Как работает подмена значений в документе — справочник с примерами
        </a>

        <div class="kdo-form">
          <div class="row">
            <div>
              <label class="caption">Название документа</label>
              <input v-model="form.title" type="text" placeholder="Приказ о предоставлении отпуска">
            </div>
            <div>
              <label class="caption">Маршрут согласования</label>
              <select v-model="form.approvalTemplateId">
                <option :value="null">— выберите —</option>
                <option v-for="approval in approvals" :key="approval.id" :value="approval.id">{{ approval.title }}</option>
              </select>
              <div v-if="!approvals.length" class="note">Сначала создайте маршрут на вкладке «Маршруты»</div>
            </div>
          </div>

          <div class="row one">
            <div>
              <label class="caption">Файл шаблона (.docx)</label>
              <div class="file-row">
                <label class="kdo-btn small file-button">
                  {{ uploading ? 'Загружаем...' : (form.fileId ? 'Заменить файл' : 'Выбрать файл') }}
                  <input type="file" accept=".docx" :disabled="uploading" @change="onFile">
                </label>
                <span v-if="form.fileName" class="file-name">{{ form.fileName }}</span>
                <span v-else class="note">Файл не выбран</span>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="section-title">Доступные поля</div>
              <input v-model="fieldSearch" class="mini-search" type="text" placeholder="Поиск поля">
              <div class="pick-list">
                <div v-for="field in availableFields" :key="field.key" class="pick-item" @click="addField(field.key)">
                  <span class="kdo-macro">{{ field.macro }}</span>
                  <span class="pick-label">{{ field.label }}</span>
                  <span class="plus">+</span>
                </div>
                <div v-if="!availableFields.length" class="note">Все поля добавлены или ничего не найдено</div>
              </div>
              <button type="button" class="kdo-btn small" style="margin-top: 8px" @click="openFieldEditor('')">+ Создать новое поле</button>
            </div>

            <div class="column">
              <div class="section-title">Поля шаблона (в порядке на форме)</div>
              <div class="pick-list">
                <div v-for="(key, index) in form.assignedMetaFields" :key="key" class="pick-item assigned">
                  <span class="kdo-macro">${{ '{' + key + '}' }}</span>
                  <span class="pick-label">{{ fieldLabel(key) }}</span>
                  <button type="button" class="icon-btn" :disabled="index === 0" @click="moveField(index, -1)">↑</button>
                  <button
                    type="button"
                    class="icon-btn"
                    :disabled="index === form.assignedMetaFields.length - 1"
                    @click="moveField(index, 1)">
                    ↓
                  </button>
                  <button type="button" class="icon-btn danger" @click="form.assignedMetaFields.splice(index, 1)">✕</button>
                </div>
                <div v-if="!form.assignedMetaFields.length" class="note">Добавьте поля слева</div>
              </div>
            </div>
          </div>

          <div class="section-title">Сверка макросов в файле</div>
          <div v-if="!form.fileId" class="note">Загрузите файл, чтобы увидеть, какие макросы в нём есть.</div>
          <div v-else-if="analyzing" class="note">Проверяем файл...</div>
          <div v-else-if="analysis">
            <div v-for="item in analysis.macros" :key="item.key" class="macro-row">
              <span class="kdo-macro">{{ item.macro }}</span>
              <span class="kdo-badge" :class="statusClass(item.status)">{{ statusTitle(item.status) }}</span>
              <span class="macro-title">{{ item.title }}</span>
              <span v-if="item.note" class="note inline">{{ item.note }}</span>
              <button
                v-if="item.status === 'fieldNotAssigned'"
                type="button"
                class="kdo-btn small"
                @click="addField(item.key)">
                Добавить в шаблон
              </button>
              <button
                v-if="item.status === 'autofillUnavailable' && item.requires"
                type="button"
                class="kdo-btn small"
                @click="addField(item.requires)">
                Добавить поле «{{ fieldLabel(item.requires) }}»
              </button>
              <button
                v-if="item.status === 'unknown'"
                type="button"
                class="kdo-btn small"
                @click="openFieldEditor(item.key)">
                Создать поле
              </button>
            </div>
            <div v-if="!analysis.macros.length" class="note">В файле нет макросов вида ${ключ}</div>
            <div v-if="analysis.unusedFields.length" class="note" style="margin-top: 8px">
              Поля, которых нет в файле (они будут на форме, но в документ не попадут):
              {{ analysis.unusedFields.map((item) => item.title).join(', ') }}
            </div>
          </div>

          <div v-if="error" class="error">{{ error }}</div>
        </div>

        <div class="kdo-actions">
          <button type="button" class="kdo-btn" :disabled="saving" @click="closeEditor">Отмена</button>
          <button type="button" class="kdo-btn primary" :disabled="saving || uploading" @click="save">
            {{ saving ? 'Сохраняем...' : 'Сохранить' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="defaultsOpen" class="kdo-overlay" @mousedown.self="defaultsOpen = false">
      <div class="kdo-dialog wide">
        <h3>Типовые документы</h3>
        <div class="kdo-hint">
          Готовые документы с маршрутом согласования и набором полей. Нажмите «Создать» — шаблон сразу появится у компании.
        </div>

        <div class="kdo-form">
          <div class="row one">
            <div>
              <label class="caption">Кто подписывает шаги «конкретный сотрудник» (например, руководитель)</label>
              <select v-model="defaultSignerId">
                <option :value="null">Руководитель компании (по умолчанию)</option>
                <option v-for="user in users" :key="user.id" :value="user.id">{{ user.fullName }}</option>
              </select>
              <div class="note">Если у компании не задан руководитель и сотрудник не выбран, исполнителя шага нужно будет назначить в маршруте.</div>
            </div>
          </div>
        </div>

        <div v-for="item in defaults" :key="item.id" class="default-card">
          <div class="default-main">
            <div class="default-title">{{ item.title }}</div>
            <div class="default-text">{{ item.description }}</div>
            <div class="default-meta">
              Маршрут: {{ item.steps.map((step) => step.title).join(' → ') }} · полей: {{ item.assignedMetaFields.length }}
            </div>
          </div>
          <div class="default-action">
            <span v-if="item.added" class="kdo-badge ok">Уже добавлен</span>
            <button
              v-else
              type="button"
              class="kdo-btn small primary"
              :disabled="creatingKey === item.key"
              @click="createFromDefault(item)">
              {{ creatingKey === item.key ? 'Создаём...' : 'Создать' }}
            </button>
            <button type="button" class="kdo-btn small danger" @click="defaultToDelete = item">Удалить</button>
          </div>
        </div>
        <div v-if="!defaults.length" class="note">Каталог типовых документов пуст</div>
        <div v-if="defaultsMessage" class="kdo-note-ok">{{ defaultsMessage }}</div>
        <div v-if="defaultsError" class="error-box">{{ defaultsError }}</div>

        <div class="kdo-actions">
          <button type="button" class="kdo-btn" @click="defaultsOpen = false">Закрыть</button>
        </div>
      </div>
    </div>

    <div v-if="makeDefault" class="kdo-overlay" @mousedown.self="makeDefault = null">
      <div class="kdo-dialog">
        <h3>Сделать типовым документом</h3>
        <div class="kdo-hint">
          В каталог попадут файл, маршрут (без конкретных исполнителей) и набор полей шаблона «{{ makeDefault.item.title }}».
        </div>
        <div class="kdo-form">
          <div class="row one">
            <div>
              <label class="caption">Название в каталоге</label>
              <input v-model="makeDefault.title" type="text">
            </div>
          </div>
          <div class="row one">
            <div>
              <label class="caption">Описание (необязательно)</label>
              <textarea v-model="makeDefault.description" rows="3"></textarea>
            </div>
          </div>
          <div v-if="makeDefaultError" class="error">{{ makeDefaultError }}</div>
        </div>
        <div class="kdo-actions">
          <button type="button" class="kdo-btn" @click="makeDefault = null">Отмена</button>
          <button type="button" class="kdo-btn primary" @click="saveMakeDefault">Добавить в каталог</button>
        </div>
      </div>
    </div>

    <c-kdo-field-editor
      v-if="fieldEditor"
      :company-id="companyId"
      :field="null"
      :preset-key="fieldEditor.presetKey"
      :dict="dict"
      :all-fields="fields"
      @saved="onFieldCreated"
      @close="fieldEditor = null" />

    <c-modal-message
      v-if="defaultToDelete"
      :title="`Удалить «${defaultToDelete.title}» из каталога типовых документов?`"
      description="Запись пропадёт из каталога для всех компаний. Уже созданные из неё шаблоны останутся."
      @agree="removeDefault"
      @cancel="defaultToDelete = null" />

    <c-modal-message
      v-if="toDelete"
      :title="`Удалить шаблон «${toDelete.title}»?`"
      description="Шаблон, по которому уже созданы документы, удалить не получится."
      @agree="remove"
      @cancel="toDelete = null" />
  </div>
</template>

<script>
export default {
  name: 'CKdoDocumentTemplates',
  props: {
    companyId: { type: [ Number, String, ], required: true, },
  },
  data() {
    return {
      rows: [],
      approvals: [],
      fields: [],
      dict: { workAs: [], valueTypes: [], dataSources: [], neededFieldsBySource: {}, },
      form: null,
      fieldSearch: '',
      uploading: false,
      analyzing: false,
      analysis: null,
      analyzeTimer: null,
      saving: false,
      error: '',
      toDelete: null,
      defaultToDelete: null,
      fieldEditor: null,
      defaultsOpen: false,
      defaults: [],
      users: [],
      defaultSignerId: null,
      creatingKey: '',
      defaultsMessage: '',
      defaultsError: '',
      makeDefault: null,
      makeDefaultError: '',
    }
  },
  computed: {
    availableFields() {
      if (!this.form) return []
      const query = this.fieldSearch.trim().toLowerCase()
      return this.fields.filter((field) => field.isActive && !this.form.assignedMetaFields.includes(field.key) &&
        (!query || field.key.toLowerCase().includes(query) || field.label.toLowerCase().includes(query)))
    },
  },
  created() {
    $api.kdo.dictionaries().then((response) => {
      this.dict = response.data.content
    })
    this.load()
    this.loadFields()
    $api.kdo.approvalTemplates(this.companyId).then((response) => {
      this.approvals = response.data.content || []
    })
  },
  methods: {
    load() {
      this.$var('loadTemplates', true)
      $api.kdo.documentTemplates(this.companyId).then((response) => {
        this.rows = response.data.content || []
      }).finally(() => {
        this.$var('loadTemplates', false)
      })
    },
    loadFields() {
      return $api.kdo.fields(this.companyId).then((response) => {
        this.fields = response.data.content || []
      })
    },
    openDefaults() {
      this.defaultsOpen = true
      this.defaultsMessage = ''
      this.defaultsError = ''
      this.loadDefaults()
      if (!this.users.length) {
        $api.kdo.users(this.companyId).then((response) => {
          this.users = response.data.content || []
        })
      }
    },
    loadDefaults() {
      return $api.kdo.companyDefaultTemplates(this.companyId).then((response) => {
        this.defaults = response.data.content || []
      })
    },
    // шаблон, маршрут и копия docx создаются на бэкенде; предупреждения показываем рядом
    createFromDefault(item) {
      this.defaultsMessage = ''
      this.defaultsError = ''
      this.creatingKey = item.key
      $api.kdo.createFromDefault(this.companyId, item.id, { signerId: this.defaultSignerId, }).then((response) => {
        const warnings = response.data.content.warnings || []
        this.defaultsMessage = `Создан шаблон «${item.title}».` + (warnings.length ? ' Обратите внимание: ' + warnings.join('; ') + '.' : '')
        this.loadDefaults()
        this.load()
        $api.kdo.approvalTemplates(this.companyId).then((res) => {
          this.approvals = res.data.content || []
        })
      }).catch((err) => {
        this.defaultsError = err?.response?.data?.message || 'Не удалось создать шаблон'
      }).finally(() => {
        this.creatingKey = ''
      })
    },
    removeDefault() {
      const item = this.defaultToDelete
      this.defaultToDelete = null
      this.defaultsError = ''
      $api.kdo.deleteDefaultTemplate(item.id).then(() => {
        $app.ui.notify('Типовой документ удалён из каталога', 'success')
        this.loadDefaults()
      }).catch((err) => {
        this.defaultsError = err?.response?.data?.message || 'Не удалось удалить типовой документ'
      })
    },
    saveMakeDefault() {
      this.makeDefaultError = ''
      $api.kdo.makeDefaultTemplate(this.makeDefault.item.id, {
        title: this.makeDefault.title,
        description: this.makeDefault.description,
      }).then(() => {
        this.makeDefault = null
        $app.ui.notify('Документ добавлен в каталог типовых', 'success')
      }).catch((err) => {
        this.makeDefaultError = err?.response?.data?.message || 'Не удалось добавить в каталог'
      })
    },
    // справочник открывается в новой вкладке, чтобы не потерять заполненную форму
    helpHref(section) {
      return this.$router.resolve({ name: 'kdo-help', hash: '#' + section, }).href
    },
    fieldLabel(key) {
      const field = $n.find(this.fields, (item) => item.key === key)
      return field ? field.label : key
    },
    openEditor(item) {
      this.error = ''
      this.analysis = null
      this.fieldSearch = ''
      // маршруты могли поменяться на соседней вкладке
      $api.kdo.approvalTemplates(this.companyId).then((response) => {
        this.approvals = response.data.content || []
      })
      if (item) {
        this.form = {
          id: item.id,
          title: item.title,
          approvalTemplateId: item.approvalTemplateId,
          fileId: item.fileId,
          fileName: item.fileName,
          assignedMetaFields: item.assignedMetaFields.slice(),
        }
      } else {
        this.form = { id: null, title: '', approvalTemplateId: null, fileId: null, fileName: '', assignedMetaFields: [], }
      }
      this.scheduleAnalyze()
    },
    closeEditor() {
      if (this.saving) return
      this.form = null
    },
    addField(key) {
      if (!this.form.assignedMetaFields.includes(key)) {
        this.form.assignedMetaFields.push(key)
        this.scheduleAnalyze()
      }
    },
    moveField(index, shift) {
      const items = this.form.assignedMetaFields
      const target = index + shift
      if (target < 0 || target >= items.length) return
      const [ moved, ] = items.splice(index, 1)
      items.splice(target, 0, moved)
    },
    onFile(event) {
      const file = event.target.files[0]
      event.target.value = ''
      if (!file) return
      this.error = ''
      this.uploading = true
      $api.kdo.uploadTemplateFile(this.companyId, file).then((response) => {
        const uploaded = response.data.content
        this.form.fileId = uploaded.id
        this.form.fileName = uploaded.name
        this.scheduleAnalyze()
      }).catch((err) => {
        this.error = err?.response?.data?.message || 'Не удалось загрузить файл'
      }).finally(() => {
        this.uploading = false
      })
    },
    scheduleAnalyze() {
      clearTimeout(this.analyzeTimer)
      this.analyzeTimer = setTimeout(() => this.analyze(), 300)
    },
    analyze() {
      if (!this.form || !this.form.fileId) {
        this.analysis = null
        return
      }
      this.analyzing = true
      $api.kdo.analyze(this.companyId, {
        fileId: this.form.fileId,
        assignedMetaFields: this.form.assignedMetaFields,
      }).then((response) => {
        this.analysis = response.data.content
      }).catch(() => {
        this.analysis = null
      }).finally(() => {
        this.analyzing = false
      })
    },
    statusTitle(status) {
      return {
        field: 'Поле шаблона',
        autofill: 'Автозаполнение',
        builtIn: 'Встроенный',
        fieldNotAssigned: 'Поле не добавлено',
        autofillUnavailable: 'Нет источника',
        unknown: 'Не найден',
      }[status] || status
    },
    statusClass(status) {
      return {
        field: 'ok',
        autofill: 'ok',
        builtIn: 'ok',
        fieldNotAssigned: 'warn',
        autofillUnavailable: 'warn',
        unknown: 'err',
      }[status]
    },
    openFieldEditor(presetKey) {
      this.fieldEditor = { presetKey, }
    },
    // новое поле сразу добавляем в шаблон: ищем его по ключу, если он был задан
    onFieldCreated() {
      const presetKey = this.fieldEditor ? this.fieldEditor.presetKey : ''
      this.fieldEditor = null
      this.loadFields().then(() => {
        if (presetKey && $n.find(this.fields, (item) => item.key === presetKey)) {
          this.addField(presetKey)
        }
      })
    },
    save() {
      this.error = ''
      this.saving = true
      const payload = {
        title: this.form.title,
        approvalTemplateId: this.form.approvalTemplateId,
        fileId: this.form.fileId,
        assignedMetaFields: this.form.assignedMetaFields,
      }
      const request = this.form.id ?
        $api.kdo.editDocumentTemplate(this.form.id, payload) :
        $api.kdo.createDocumentTemplate(this.companyId, payload)
      request.then(() => {
        this.form = null
        this.load()
      }).catch((err) => {
        this.error = err?.response?.data?.message || 'Не удалось сохранить шаблон'
      }).finally(() => {
        this.saving = false
      })
    },
    remove() {
      const item = this.toDelete
      this.toDelete = null
      this.$var('loadTemplates', true)
      $api.kdo.deleteDocumentTemplate(item.id).then(() => {
        this.load()
      }).catch(() => {
        this.$var('loadTemplates', false)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './kdo.scss';

.grow {
  flex: 1;
  margin: 0;
}

.actions {
  white-space: nowrap;
  text-align: right;

  .kdo-btn {
    margin-left: 6px;
  }
}

.file-row {
  display: flex;
  align-items: center;
  gap: 12px;

  .file-button {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;

    input {
      position: absolute;
      inset: 0;
      opacity: 0;
      cursor: pointer;
    }
  }

  .file-name {
    font-weight: 600;
    color: #2A2766;
  }
}

.columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }

  .mini-search {
    margin-bottom: 8px;
  }
}

.pick-list {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #ece4fb;
  border-radius: 12px;
  background: #faf8ff;
  padding: 6px;
}

.pick-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #f0e9ff;
  }

  &.assigned {
    cursor: default;

    &:hover {
      background: transparent;
    }
  }

  .pick-label {
    flex: 1;
    font-size: 13px;
    min-width: 0;
  }

  .plus {
    color: var(--primary);
    font-weight: 700;
  }

  .icon-btn {
    width: 26px;
    height: 26px;
    min-width: 26px;
  }
}

.default-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #ece4fb;
  border-radius: 14px;
  background: #faf8ff;
  margin-bottom: 10px;

  .default-main {
    flex: 1;
    min-width: 0;
  }

  .default-action {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  .default-title {
    font-weight: 700;
    color: #2A2766;
  }

  .default-text {
    font-size: 13px;
    color: #4a4570;
    margin-top: 2px;
  }

  .default-meta {
    font-size: 12px;
    color: #8f88a8;
    margin-top: 4px;
  }
}

.kdo-note-ok {
  background: #DDF7E3;
  color: #14602a;
  border-radius: 10px;
  padding: 10px 14px;
  margin-top: 8px;
  font-size: 14px;
}

.error-box {
  background: #FDE2E2;
  color: #C62828;
  border-radius: 10px;
  padding: 10px 14px;
  margin-top: 8px;
  font-size: 14px;
}

.macro-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid #f0ecf8;

  .macro-title {
    font-size: 13px;
  }

  .note.inline {
    margin: 0;
    flex: 1;
  }
}
</style>
